import { appStore } from "Stores/index";
import {observable, flow, makeObservable} from "mobx";
import { EAIClient } from "Services/EAIClient";
import { AttachmentFile } from "Components/Common/AttachmentSelector";
import * as _ from "lodash";
import {ACCEPTED_MIMETYPES} from "../../../utils/constans";

export interface AcceptedMimetype{
    extension: string
    mimetype: string
    icon: string
}

// typescript doesn't have bindings for FileSystem api
export interface FileSystemEntry {
    name: Readonly<string>
    fullPath: Readonly<string>
    isDirectory: Readonly<boolean>
    isFile: Readonly<boolean>
}

export interface FileSystemFileEntry extends FileSystemEntry{
    file: (
        onSuccess: (file: File) => void,
        onError?: () => void
    ) => void,
}

export interface FileSystemDirectoryReader{
    readEntries: (
        onSuccess: (entries: FileSystemEntry[]) => void
    ) => void
}

export interface FileSystemDirectoryEntry extends FileSystemEntry{
    createReader: () => FileSystemDirectoryReader
}

export interface Attachment {
    id: string
    file_name: string
    uploaded_by: string
    file_type: string
    file_icon: string | undefined
    created: string
    status: "pending" | "accepted"
    permissions: {
        can_delete: boolean
    }
    uploaded_by_name: string
    uploaded_by_email: string
}

export class AttachmentStore {
    client = new EAIClient();

    @observable isFetchingAttachments = false;
    @observable isDownloadingAttachment: false | string = false;
    @observable isUploading = false;
    @observable attachments: Attachment[] = [];
    @observable currentActionPlan: string | undefined = undefined;
    @observable acceptedMimetypes: AcceptedMimetype[] = [];
    @observable mimetypesRegex: RegExp | undefined;
    @observable maxAttachmentSizeMb = -1;
    @observable maxStorageGb = -1;

    @observable mimetypeFilterOptions: any[] = [];

    constructor() {
        makeObservable(this);
    }

    @flow
    * getAllAttachmentsForActionplan(actionplanId: string, forceRefresh = false) {
        if(actionplanId !== this.currentActionPlan || forceRefresh){
            this.isFetchingAttachments = true;
            try {
                const resp = yield this.client.getAllAttachmentsForActionplan(actionplanId);
                this.attachments = resp.data.data;
                this.currentActionPlan = actionplanId;
                return this.attachments;
            } catch(e) {
                appStore.addError(e);
            } finally {
                this.isFetchingAttachments = false;
            }
        }

        return this.attachments;
    }

    @flow
    * downloadAttachment(attachmentId: string): any{
        this.isDownloadingAttachment = attachmentId;
        try{
            const resp = yield this.client.getAttachmentDownloadLink(attachmentId);
            if(resp.data.data.download_url){
                const link = document.createElement("a");
                link.href = resp.data.data.download_url;
                link.click();
            } else {
                appStore.addError(new Error(),"Attachment is being processed. Please try again later.");
            }
        } catch(e){
            appStore.addError(e);
        } finally {
            this.isDownloadingAttachment = false;
        }
    }

    @flow
    * addAttachmentsToActionplan(actionplanId: string, attachmentIds: string[]): any{
        this.isFetchingAttachments = true;
        try{
            yield this.client.addAttachmentToActionplan(actionplanId, attachmentIds);
        } catch(e){
            appStore.addError(e);
        } finally {
            this.isFetchingAttachments = false;
        }
    }

    @flow
    * deleteAttachment(attachmentId: string): any{
        this.isFetchingAttachments = true;
        try{
            yield this.client.deleteAttachment(attachmentId);
        } catch(e){
            appStore.addError(e);
        } finally {
            this.isFetchingAttachments = false;
        }
    }

    @flow
    * deleteAttachmentFromActionplan(actionplanId: string, fileId: string): any{
        this.isFetchingAttachments = true;
        try{
            yield this.client.deleteAttachmentFromActionplan(actionplanId, fileId);
        } catch(e){
            appStore.addError(e);
        } finally {
            this.isFetchingAttachments = false;
            this.getAllAttachmentsForActionplan(actionplanId, true);
        }
    }

    @flow
    * deleteAttachmentFromComment(commentId: string, fileId: string): any{
        this.isFetchingAttachments = true;
        try{
            yield this.client.deleteAttachmentFromComment(commentId, fileId);
        } catch(e){
            appStore.addError(e, "Error removing attachment. Please try again later.");
        } finally {
            this.isFetchingAttachments = false;
        }
    }

    @flow
    * getAcceptedMimetypes(forceRefresh = false): any{
        if(this.acceptedMimetypes.length === 0 || forceRefresh){
            try {
                this.acceptedMimetypes = ACCEPTED_MIMETYPES;

                const uniqueMimetypesByExtensions = _.uniqBy(this.acceptedMimetypes, "extension");

                const uniqueExtensions = uniqueMimetypesByExtensions.map((am: AcceptedMimetype) => am.extension);
                this.mimetypesRegex = new RegExp(`.+\.(${uniqueExtensions.join("|")})$`, "i");

                const ALL_VALUE = {label:"All", value:"all"};
                const grouped: any[] = _.uniqBy(
                    uniqueMimetypesByExtensions.map(am => ({
                        icon:am.icon,
                        value:`group__${am.icon}`,
                        types:[],
                    })),
                    "icon"
                );

                for (let idx = 0; idx < uniqueMimetypesByExtensions.length; idx++) {
                    const grpIdx = _.findIndex(grouped, {icon:uniqueMimetypesByExtensions[idx].icon});
                    if(grpIdx !== undefined){
                        grouped[grpIdx].types.push(uniqueMimetypesByExtensions[idx].extension);
                        grouped.splice(grpIdx + 1, 0, {
                            label:uniqueMimetypesByExtensions[idx].extension,
                            value:uniqueMimetypesByExtensions[idx].extension,
                        });
                    }
                }

                grouped.unshift(ALL_VALUE);
                this.mimetypeFilterOptions = grouped;

            } catch(e) {
                appStore.addError(e, "Unable to fetch accepted file types. Please try again.");
            }
        }
    }

    @flow
    * createAndUploadAttachments(files: AttachmentFile[]): any{
        this.isUploading = true;
        const ret = yield this.client.createAndUploadAttachments(files);
        this.isUploading = false;
        return ret;
    }
}
